<script lang="ts" setup>
import type { ChartData, ChartOptions } from 'chart.js'
import {
  AliyahType,
  BarMitzvahType,
  HaftarahMelodyType,
  HaftarahType,
  PronunciationType,
  SentenceGroupMelodyType,
  SentenceGroupType,
  SentenceType,
  TagType,
  TorahMelodyType,
} from 'types/types'
import { computed, toRef, watch } from 'vue'
import { Line } from 'vue-chartjs'

import AnnotatedText from '@/components/AnnotatedText.vue'
import PopOver from '@/components/PopOver.vue'
import RecordingControls from '@/components/RecordingControls.vue'
import SamplingOverlay from '@/components/SamplingOverlay.vue'
import VoiceStyleSelector from '@/components/VoiceStyleSelector.vue'
import { useFont } from '@/composables/font'
import { useAnnotations } from '@/composables/useAnnotations'
import { usePopover } from '@/composables/usePopover'
import { usePopoverPlayback } from '@/composables/usePopoverPlayback'
import { PHRASE_LEVEL_SENTENCE, PHRASE_LEVEL_WORDS, type PhraseLevelInfo } from '@/composables/versePhraser'
import { FOCUS_TEXT_NAME, focusTextSegmentId } from '@/constants/practiceConstants'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { segmentPredicateAsWordPredicate } from '@/util/annotatedTextCallbacks'
import { AnnotatedWord } from '@/util/AnnotatedWord'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import {
  regularStyle,
  SEGMENT_STYLES,
  segmentBoundaryStyle as defaultSegmentBoundaryStyle,
  wordBoundaryStyle as defaultWordBoundaryStyle,
} from '@/util/TextStyles'
import { TransformationType } from '@/util/Transformations'
import { inSingMode, VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ props
interface Props {
  focusSentence: SentenceType | null
  focusSentenceIndex: number | null
  annotatedSegments: Array<Array<AnnotatedSegment>>
  availableLevels: PhraseLevelInfo[]
  barMitzvah: BarMitzvahType | null
  aliyah: AliyahType | null
  haftarah: HaftarahType | null
  sentenceGroup: SentenceGroupType | null
  voiceStyleSet: VoiceStyleSet
  existingSentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios | null
  followerSegmentStyle: (style: string, segment: AnnotatedSegment, sentenceIndex: number, segmentIndex: number) => string
  transformation: TransformationType
  segmentations: Array<Array<number>>
  isRecording: boolean
  isReadyForRecording: boolean
  uploadWaiting: boolean
  pendingUploadRecording: File | null
  backgroundSampling: boolean
  backgroundSamplingPercent: number
  currentSegmentIndex: number
  disableAutomaticVoiceStartStop: boolean
  displayGraph: boolean
  phraseLevel: number
  segmentationLength: number
  pronunciationChoices: Array<PronunciationType>
  torahMelodyChoices: Array<TorahMelodyType>
  haftarahMelodyChoices: Array<HaftarahMelodyType>
  sentenceGroupMelodyChoices: Array<SentenceGroupMelodyType>
  nonHiddenUserTags: Array<TagType>
  inReadMode: boolean
  isMelodicStyleDisabled: boolean
  phrasingDropdownDisabled: boolean
  segmentableSentence: boolean
  showStartNow: boolean
  chartData: ChartData<'line'>
  chartOptions: ChartOptions<'line'>
  paused: boolean
  playing: boolean
  userIsLoggedIn: boolean
  canProceedToPrevious: boolean
  canProceedToNext: boolean
}

const props = defineProps<Props>()
// }}}

// {{{ emits
interface Emits {
  (e: 'proceedToPrevious'): void
  (e: 'proceedToNext'): void
  (e: 'close'): void
  (e: 'updateVoiceStyleSet', value: VoiceStyleSet): void
  (e: 'startRecord', mode: 'single' | 'continuous'): void
  (e: 'toggleRecord'): void
  (e: 'uploadFile'): void
  (e: 'updatePendingUploadRecording', value: File | null): void
  (e: 'updateDisableAutomaticVoiceStartStop', value: boolean): void
  (e: 'updateDisplayGraph', value: boolean): void
  (e: 'updatePhraseLevel', value: number): void
  (
    e: 'clickSegmentBoundary',
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): void
  (
    e: 'clickWordBoundary',
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    annotatedWord: AnnotatedWord,
    sentenceIndex: number,
    segmentIndex: number,
    wordIndex: number,
  ): void
  (e: 'playSegment', segmentIndex: number): void
  (e: 'reRecordSegment', segmentIndex: number): void
  (e: 'pauseSegment'): void
  (e: 'resumeSegment'): void
  (e: 'stopSegment'): void
  (e: 'warnManualSegmentation'): void
}

const emit = defineEmits<Emits>()
// }}}

// {{{ composables
const { font, fontSize, lineHeight } = useFont()
const { annotations } = useAnnotations(computed(() => props.barMitzvah))
// }}}

// {{{ popover state
const playback = usePopoverPlayback<number>({
  paused: toRef(props, 'paused'),
  playFromStart: (segmentIndex) => emit('playSegment', segmentIndex),
  resume: () => emit('resumeSegment'),
})

const {
  close: closePopover,
  data: popoverData,
  open: openPopover,
  visible: popoverVisible,
} = usePopover<{ segmentIndex: number; sentenceIndex: number }>({
  onBeforeClose: () => {
    emit('stopSegment')
    playback.reset()
  },
})

const popoverTarget = computed<string | null>(() => {
  // "Sing/Read Now!" popover targets the first segment
  if (props.showStartNow) {
    return focusTextSegmentId(0, 0)
  }
  if (!popoverData.value) return null
  return focusTextSegmentId(popoverData.value.sentenceIndex, popoverData.value.segmentIndex)
})

const isPopoverVisible = computed(() => props.showStartNow || popoverVisible.value)

// Auto-close popover on sentence navigation
watch(
  () => props.focusSentenceIndex,
  () => {
    if (popoverVisible.value) {
      closePopover()
    }
  },
)
// }}}

// {{{ computed
const focusSentences = computed(() => {
  return props.focusSentence ? [props.focusSentence] : []
})

const hasSegmentRecording = (
  _annotatedSentence: AnnotatedSentence | null,
  _annotatedSegment: AnnotatedSegment | null,
  _sentenceIndex: number,
  _segmentIndex: number,
): boolean => {
  return !props.isRecording && !!props.existingSentenceRecordingMetadataWithPitchedAudios && hasExplicitSegmentation()
}

const hasSegmentRecordingWordWrapper = segmentPredicateAsWordPredicate(hasSegmentRecording)

const clickableBoundary = () => {
  return (
    !props.isRecording &&
    props.phraseLevel > PHRASE_LEVEL_SENTENCE &&
    props.phraseLevel < PHRASE_LEVEL_WORDS &&
    !props.existingSentenceRecordingMetadataWithPitchedAudios &&
    props.segmentableSentence
  )
}
// }}}

// {{{ handlers

// Opens popover AND immediately plays.
const openPopoverAndPlay = (sentenceIndex: number, segmentIndex: number) => {
  console.log('SentenceFocus.openPopoverAndPlay', { segmentIndex, sentenceIndex })
  openPopover({ segmentIndex, sentenceIndex })
  playback.start(segmentIndex)
}

// Both segment and word clicks open popover + play.
// The per-tick guard in playback.start prevents double-play from event bubbling.
const handleClickSegment = (
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
) => {
  if (!hasSegmentRecording(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)) return
  openPopoverAndPlay(sentenceIndex, segmentIndex)
}

// Word click — opens popover AND plays immediately
const handleClickWord = (
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  _annotatedWord: AnnotatedWord,
  sentenceIndex: number,
  segmentIndex: number,
  _wordIndex: number,
) => {
  console.log('SentenceFocus.handleClickWord', {
    hasRec: hasSegmentRecording(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex),
    segmentIndex,
    sentenceIndex,
  })
  if (!hasSegmentRecording(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)) return
  openPopoverAndPlay(sentenceIndex, segmentIndex)
}

const handlePopoverPlay = () => {
  const segmentIndex = popoverData.value?.segmentIndex
  if (segmentIndex === undefined || segmentIndex === null) {
    return
  }
  playback.playOrResume(segmentIndex)
}

const handlePopoverRestart = () => {
  const segmentIndex = popoverData.value?.segmentIndex
  if (segmentIndex === undefined || segmentIndex === null) {
    return
  }
  playback.start(segmentIndex)
}

// Only apply alternating segment colors when there's explicit phrase segmentation.
// With empty segmentation, AnnotatedSentence creates one segment per word by default —
// we don't want alternating colors for that.
const hasExplicitSegmentation = (): boolean => props.segmentations.length > 0

const segmentStyle = (
  style: string,
  _annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
): string => {
  const baseStyle = hasExplicitSegmentation() ? SEGMENT_STYLES[segmentIndex % 2] : style
  if (props.isRecording) {
    return props.followerSegmentStyle(baseStyle, annotatedSegment, sentenceIndex, segmentIndex)
  }
  return baseStyle
}

const phraseBoundaryStyle = (
  style: string,
  _annotatedSentence: AnnotatedSentence,
  _annotatedSegment: AnnotatedSegment,
  _sentenceIndex: number,
  _segmentIndex: number,
): string => {
  return clickableBoundary() ? defaultSegmentBoundaryStyle : style
}

const wordBoundaryStyle = (
  style: string,
  _annotatedSentence: AnnotatedSentence,
  _annotatedSegment: AnnotatedSegment,
  _annotatedWord: AnnotatedWord,
  _sentenceIndex: number,
  _segmentIndex: number,
  _wordIndex: number,
): string => {
  return clickableBoundary() ? defaultWordBoundaryStyle : regularStyle
}
// }}}
</script>

<template>
  <v-card class="d-flex flex-column" :ripple="false" style="max-height: 90vh">
    <v-card-text class="text-high-emphasis flex-grow-1 overflow-y-auto">
      <!-- Navigation Controls -->
      <div class="focus-nav-row mb-2">
        <div class="focus-nav-prev">
          <v-btn
            id="prev_link"
            color="primary"
            :disabled="!canProceedToPrevious"
            size="small"
            variant="outlined"
            @click="emit('proceedToPrevious')"
          >
            <v-icon left size="small">mdi-skip-backward</v-icon>
            Prev
          </v-btn>
        </div>
        <div class="focus-nav-next-group">
          <v-btn
            id="next_link"
            color="primary"
            :disabled="!canProceedToNext"
            size="small"
            variant="outlined"
            @click="emit('proceedToNext')"
          >
            Next
            <v-icon right size="small">mdi-skip-forward</v-icon>
          </v-btn>
        </div>
        <v-btn class="focus-nav-close" icon size="small" variant="text" @click="emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Annotated Text Display -->
      <div class="mt-2 pt-2 text-center">
        <span ref="focusSentenceSpan">
          <annotated-text
            v-if="userIsLoggedIn"
            :annotated-segments="annotatedSegments as Array<Array<AnnotatedSegment>>"
            :annotations="annotations"
            :clickable-segment="hasSegmentRecording"
            :clickable-segment-boundary="clickableBoundary"
            :clickable-word="hasSegmentRecordingWordWrapper"
            :clickable-word-boundary="clickableBoundary"
            :default-font="font"
            :font-size="fontSize"
            :line-height="lineHeight"
            :name="FOCUS_TEXT_NAME"
            :segment-boundary-style="phraseBoundaryStyle"
            :segment-style="segmentStyle"
            :segmentations="segmentations"
            :sentences="focusSentences"
            :show-end-of-sentence-character="true"
            :show-sentence-numbers="false"
            :start-index="focusSentenceIndex as number"
            :suppress-colors="
              (_si, gi) => {
                const result = isRecording && gi === currentSegmentIndex
                if (result) console.log('suppressColors: hiding colors for segment', gi)
                return result
              }
            "
            :transformation="transformation"
            :voice-style-set="voiceStyleSet"
            :word-boundary-style="wordBoundaryStyle"
            @click-segment="handleClickSegment"
            @click-segment-boundary="(as, ag, si, gi) => emit('clickSegmentBoundary', as, ag, si, gi)"
            @click-word="handleClickWord"
            @click-word-boundary="(as, ag, aw, si, gi, wi) => emit('clickWordBoundary', as, ag, aw, si, gi, wi)"
          />
          <pop-over :attach-id="popoverTarget" :visible="isPopoverVisible">
            <v-card>
              <v-card-text class="pa-1">
                <template v-if="showStartNow">
                  <span class="mx-4 my-5 text-h5">{{ inSingMode(voiceStyleSet) ? 'Sing' : 'Read' }} now!</span>
                </template>
                <template v-else>
                  <v-progress-circular v-if="uploadWaiting" indeterminate size="40" />
                  <template v-else>
                    <v-icon v-if="!playing" class="playable" size="xxx-large" @click="handlePopoverPlay"> mdi-play mdi-rotate-180 </v-icon>
                    <v-icon v-else class="pausable" size="xxx-large" @click="emit('pauseSegment')">mdi-pause</v-icon>
                    <v-icon class="playable" size="xxx-large" @click="handlePopoverRestart">mdi-restart</v-icon>
                    <v-icon
                      v-if="!isRecording && isReadyForRecording"
                      :color="playing ? 'grey-lighten-1' : 'red'"
                      :disabled="playing"
                      size="xxx-large"
                      @click="
                        () => {
                          const si = popoverData?.segmentIndex
                          if (si !== undefined) {
                            closePopover()
                            emit('reRecordSegment', si)
                          }
                        }
                      "
                    >
                      mdi-record
                    </v-icon>
                  </template>
                  <v-icon size="xxx-large" @click="closePopover()">mdi-close</v-icon>
                </template>
              </v-card-text>
            </v-card>
          </pop-over>
        </span>
      </div>

      <!-- Voice Graph -->
      <v-container v-if="displayGraph && isRecording">
        <v-row class="mt-3 pt-3" justify="center">
          <v-col align="center" md="12">
            <Line :data="chartData" :options="chartOptions" />
          </v-col>
        </v-row>
      </v-container>

      <v-divider class="my-5" />

      <!-- Voice Style Selector -->
      <voice-style-selector
        :aliyah="aliyah"
        :available-levels="availableLevels"
        :existing-sentence-recording-metadata-with-pitched-audios="existingSentenceRecordingMetadataWithPitchedAudios"
        :haftarah="haftarah"
        :haftarah-melody-choices="haftarahMelodyChoices"
        :in-read-mode="inReadMode"
        :is-melodic-style-disabled="isMelodicStyleDisabled"
        :is-recording="isRecording"
        :non-hidden-user-tags="nonHiddenUserTags"
        :phrase-level="phraseLevel"
        :phrasing-dropdown-disabled="phrasingDropdownDisabled"
        :pronunciation-choices="pronunciationChoices"
        :sentence-group="sentenceGroup"
        :sentence-group-melody-choices="sentenceGroupMelodyChoices"
        :torah-melody-choices="torahMelodyChoices"
        :voice-style-set="voiceStyleSet"
        @update:phrase-level="(v: number) => emit('updatePhraseLevel', v)"
        @update:voice-style-set="emit('updateVoiceStyleSet', $event)"
      />

      <v-divider class="my-5" />

      <!-- Recording Controls -->
      <recording-controls
        :background-sampling="backgroundSampling"
        :disable-automatic-voice-start-stop="disableAutomaticVoiceStartStop"
        :display-graph="displayGraph"
        :existing-sentence-recording-metadata-with-pitched-audios="existingSentenceRecordingMetadataWithPitchedAudios"
        :is-ready-for-recording="isReadyForRecording"
        :is-recording="isRecording"
        :pending-upload-recording="pendingUploadRecording"
        :phrase-level="phraseLevel"
        :playing="playing"
        :segmentation-length="segmentationLength"
        :upload-waiting="uploadWaiting"
        @start-record="(mode) => emit('startRecord', mode)"
        @toggle-record="emit('toggleRecord')"
        @update:disable-automatic-voice-start-stop="emit('updateDisableAutomaticVoiceStartStop', $event)"
        @update:display-graph="emit('updateDisplayGraph', $event)"
        @update:pending-upload-recording="emit('updatePendingUploadRecording', $event)"
        @upload-file="emit('uploadFile')"
        @warn-manual-segmentation="emit('warnManualSegmentation')"
      />
    </v-card-text>
  </v-card>

  <sampling-overlay
    v-if="backgroundSampling"
    :on-click="() => emit('toggleRecord')"
    :percent="backgroundSamplingPercent"
    :visible="backgroundSampling"
  />
</template>

<style scoped>
.focus-nav-row {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.focus-nav-prev {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  padding-right: 16px;
}

.focus-nav-next-group {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 16px;
}

.focus-nav-close {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}
</style>
